/**
 * GoodGuardian — Print / PDF Export Utility
 *
 * Opens a styled print window so the user can save a court-ready PDF
 * via their browser's built-in "Print → Save as PDF" feature.
 * No npm dependencies required.
 */

const BRAND = '#1F497D'

function escHtml(str = '') {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// ── Communication Log export ───────────────────────────────────────────────────
export function printCommsLog({ userName, state, entries }) {
  const TONE_LABELS = {
    neutral:     '😐 Neutral',
    cooperative: '🤝 Cooperative',
    hostile:     '⚠️ Hostile',
    concerning:  '🚩 Concerning',
  }
  const COMM_LABELS = {
    text:      '📱 Text message',
    email:     '📧 Email',
    verbal:    '🗣️ Verbal / in-person',
    app:       '💬 Co-parenting app',
    voicemail: '📞 Voicemail',
    other:     '📎 Other',
  }

  const rows = entries.map((e, i) => `
    <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
      <td>${escHtml(e.date)}${e.time ? '<br><small>' + escHtml(e.time) + '</small>' : ''}</td>
      <td>${escHtml(COMM_LABELS[e.type] || e.type)}</td>
      <td>${escHtml(e.summary)}</td>
      <td>${escHtml(TONE_LABELS[e.tone] || e.tone)}</td>
      <td>${escHtml(e.witnesses || '—')}</td>
    </tr>`).join('')

  const stats = {
    total:      entries.length,
    hostile:    entries.filter(e => e.tone === 'hostile').length,
    concerning: entries.filter(e => e.tone === 'concerning').length,
  }

  const html = buildPrintPage({
    title:    'Co-Parenting Communication Log',
    subtitle: `Prepared by: ${userName || 'GoodGuardian User'}  |  Jurisdiction: ${state || 'Not specified'}  |  Printed: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    statsHtml: `
      <div class="stats">
        <div class="stat"><span class="num">${stats.total}</span>Total Entries</div>
        <div class="stat warn"><span class="num">${stats.hostile}</span>Hostile</div>
        <div class="stat warn"><span class="num">${stats.concerning}</span>Concerning</div>
      </div>`,
    tableHtml: `
      <table>
        <thead>
          <tr>
            <th style="width:12%">Date / Time</th>
            <th style="width:16%">Type</th>
            <th style="width:42%">Summary</th>
            <th style="width:14%">Tone</th>
            <th style="width:16%">Witnesses</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`,
    disclaimer: 'This communication log was generated by GoodGuardian (good-guardian.net). Entries reflect the account holder\'s contemporaneous records. This document has not been reviewed by a licensed attorney. Share with your attorney before submitting to any court.',
  })

  openPrintWindow(html)
}

// ── Event Log export ───────────────────────────────────────────────────────────
export function printEventLog({ userName, state, events }) {
  const EVENT_LABELS = {
    exchange:  '🔄 Custody Exchange',
    violation: '🚫 Custody Violation',
    missed:    '❌ Missed Visit',
    incident:  '⚠️ Notable Incident',
    medical:   '🏥 Medical Event',
    school:    '🎓 School Event',
    other:     '📌 Other',
  }

  const rows = events.map((e, i) => `
    <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
      <td>${escHtml(e.date)}${e.time ? '<br><small>' + escHtml(e.time) + (e.endTime ? ' – ' + escHtml(e.endTime) : '') + '</small>' : ''}</td>
      <td>${escHtml(EVENT_LABELS[e.type] || e.type)}</td>
      <td>${escHtml(e.location || '—')}</td>
      <td>${escHtml(e.description)}</td>
      <td>${escHtml(e.witnesses || '—')}</td>
      <td style="text-align:center">${e.evidence ? '✅' : '—'}</td>
    </tr>`).join('')

  const stats = {
    total:      events.length,
    violations: events.filter(e => e.type === 'violation').length,
    missed:     events.filter(e => e.type === 'missed').length,
  }

  const html = buildPrintPage({
    title:    'Custody Event Log',
    subtitle: `Prepared by: ${userName || 'GoodGuardian User'}  |  Jurisdiction: ${state || 'Not specified'}  |  Printed: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    statsHtml: `
      <div class="stats">
        <div class="stat"><span class="num">${stats.total}</span>Total Events</div>
        <div class="stat warn"><span class="num">${stats.violations}</span>Violations</div>
        <div class="stat warn"><span class="num">${stats.missed}</span>Missed Visits</div>
      </div>`,
    tableHtml: `
      <table>
        <thead>
          <tr>
            <th style="width:11%">Date / Time</th>
            <th style="width:15%">Event Type</th>
            <th style="width:14%">Location</th>
            <th style="width:40%">Description</th>
            <th style="width:13%">Witnesses</th>
            <th style="width:7%">Evidence</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`,
    disclaimer: 'This custody event log was generated by GoodGuardian (good-guardian.net). Entries reflect the account holder\'s contemporaneous records and have been timestamped at the time of entry. This document has not been reviewed by a licensed attorney. Share with your attorney before submitting to any court.',
  })

  openPrintWindow(html)
}

// ── Calendar Events export ───────────────────────────────────────────────────
export function printCalendar({ userName, state, events }) {
  const TYPE_LABELS = {
    'custody-exchange': '🔄 Custody Exchange',
    'appointment':      '🏥 Appointment',
    'school':           '🎓 School Event',
    'court':            '⚖️ Court Hearing',
    'compliance':       '📋 Order Compliance',
    'other':            '📌 Other',
  }

  const sorted = [...events].sort((a, b) => a.start_date.localeCompare(b.start_date))

  const rows = sorted.map((e, i) => `
    <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
      <td>${escHtml(e.start_date)}${e.start_time ? '<br><small>' + escHtml(e.start_time) + '</small>' : ''}${e.end_date && e.end_date !== e.start_date ? '<br><small>→ ' + escHtml(e.end_date) + '</small>' : ''}</td>
      <td>${escHtml(TYPE_LABELS[e.type] || e.type)}</td>
      <td>${escHtml(e.title)}</td>
      <td>${escHtml(e.location || '—')}</td>
      <td>${escHtml(e.visibility === 'private' ? '🔒 Private' : '🔗 Shared')}</td>
      <td>${escHtml(e.notes || '—')}</td>
    </tr>`).join('')

  const stats = {
    total:     events.length,
    exchanges: events.filter(e => e.type === 'custody-exchange').length,
    court:     events.filter(e => e.type === 'court').length,
  }

  const html = buildPrintPage({
    title:    'Custody Calendar Report',
    subtitle: `Prepared by: ${userName || 'GoodGuardian User'}  |  Jurisdiction: ${state || 'Not specified'}  |  Printed: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    statsHtml: `
      <div class="stats">
        <div class="stat"><span class="num">${stats.total}</span>Total Events</div>
        <div class="stat"><span class="num">${stats.exchanges}</span>Exchanges</div>
        <div class="stat warn"><span class="num">${stats.court}</span>Court Dates</div>
      </div>`,
    tableHtml: `
      <table>
        <thead>
          <tr>
            <th style="width:13%">Date / Time</th>
            <th style="width:16%">Type</th>
            <th style="width:25%">Title</th>
            <th style="width:16%">Location</th>
            <th style="width:10%">Visibility</th>
            <th style="width:20%">Notes</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`,
    disclaimer: 'This calendar report was generated by GoodGuardian (good-guardian.net). Events reflect the account holder\'s scheduled custody exchanges, appointments, and court dates. Shared events are visible to both co-parents. This document has not been reviewed by a licensed attorney.',
  })

  openPrintWindow(html)
}

// ── Expense Log export ────────────────────────────────────────────────────────
export function printExpenseLog({ userName, state, expenses, currentUserId }) {
  const CAT_LABELS = {
    medical: '🏥 Medical', education: '📚 Education', extracurricular: '⚽ Extracurricular',
    clothing: '👕 Clothing', food: '🍽️ Food', childcare: '🧸 Childcare',
    transport: '🚗 Transport', other: '📎 Other',
  }
  const STATUS_LABELS = {
    pending: 'Open', approved: 'Approved', rejected: 'Rejected', paid: 'Paid',
  }

  function fmtMoney(n) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
  }

  const rows = expenses.map((e, i) => {
    const isMyExpense = e.submitted_by === currentUserId
    const partnerOwes = e.total_amount * (100 - e.payer_pct) / 100
    const iOwe = e.total_amount * e.payer_pct / 100
    return `
    <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
      <td>${escHtml(e.purchase_date || '—')}</td>
      <td>${escHtml(e.title)}</td>
      <td>${escHtml(CAT_LABELS[e.category] || e.category)}</td>
      <td style="text-align:right">${fmtMoney(e.total_amount)}</td>
      <td>${e.payer_pct}/${100 - e.payer_pct}</td>
      <td style="text-align:right">${isMyExpense ? fmtMoney(partnerOwes) : fmtMoney(iOwe)}</td>
      <td>${escHtml(STATUS_LABELS[e.status] || e.status)}${e.payment_method ? ' · ' + escHtml(e.payment_method) : ''}</td>
    </tr>`
  }).join('')

  // Compute totals
  const totals = expenses.reduce((acc, e) => {
    acc.total += e.total_amount || 0
    const isMyExpense = e.submitted_by === currentUserId
    if (isMyExpense) acc.theyOwe += e.total_amount * (100 - e.payer_pct) / 100
    else             acc.youOwe  += e.total_amount * e.payer_pct / 100
    return acc
  }, { total: 0, theyOwe: 0, youOwe: 0 })

  const stats = {
    total:    expenses.length,
    totalAmt: fmtMoney(totals.total),
    net:      fmtMoney(Math.abs(totals.theyOwe - totals.youOwe)),
  }

  const html = buildPrintPage({
    title:    'Child Expense Report',
    subtitle: `Prepared by: ${userName || 'GoodGuardian User'}  |  Jurisdiction: ${state || 'Not specified'}  |  Printed: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    statsHtml: `
      <div class="stats">
        <div class="stat"><span class="num">${stats.total}</span>Expenses</div>
        <div class="stat"><span class="num">${stats.totalAmt}</span>Total Amount</div>
        <div class="stat warn"><span class="num">${stats.net}</span>Net Balance</div>
      </div>`,
    tableHtml: `
      <table>
        <thead>
          <tr>
            <th style="width:10%">Date</th>
            <th style="width:22%">Expense</th>
            <th style="width:14%">Category</th>
            <th style="width:12%;text-align:right">Amount</th>
            <th style="width:8%">Split</th>
            <th style="width:14%;text-align:right">Owed</th>
            <th style="width:14%">Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`,
    disclaimer: 'This expense report was generated by GoodGuardian (good-guardian.net). Expenses reflect submissions by both co-parents with approval workflow. This document has not been reviewed by a licensed attorney. Share with your attorney before submitting to any court.',
  })

  openPrintWindow(html)
}

// ── Comprehensive Court Report ───────────────────────────────────────────────
export function printCourtReport({ userName, state, comms, events, calendarEvents, expenses, children, currentUserId, dateFrom, dateTo }) {
  const printDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null
  const rangeLabel = (dateFrom || dateTo)
    ? ` | Period: ${fmtDate(dateFrom) || 'All records'} – ${fmtDate(dateTo) || 'Present'}`
    : ''
  const sections = []

  // ── Children Summary ──
  if (children?.length > 0) {
    const kidRows = children.map((c, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${escHtml(c.name)}</td>
        <td>${escHtml(c.dob || '—')}</td>
        <td>${escHtml(c.age ? c.age + ' years' : '—')}</td>
        <td>${escHtml(c.school || '—')}</td>
        <td>${escHtml(c.medical_notes || c.allergies || '—')}</td>
      </tr>`).join('')
    sections.push(`
      <h2 class="section-title">Children (${children.length})</h2>
      <table>
        <thead><tr><th>Name</th><th>DOB</th><th>Age</th><th>School</th><th>Medical/Notes</th></tr></thead>
        <tbody>${kidRows}</tbody>
      </table>`)
  }

  // ── Communication Log Summary ──
  if (comms?.length > 0) {
    const hostileCount = comms.filter(e => e.tone === 'hostile').length
    const concerningCount = comms.filter(e => e.tone === 'concerning').length
    const recentComms = [...comms].sort((a, b) => b.date?.localeCompare(a.date)).slice(0, 20)
    const commRows = recentComms.map((e, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${escHtml(e.date)}</td>
        <td>${escHtml(e.summary?.slice(0, 80))}${e.summary?.length > 80 ? '…' : ''}</td>
        <td>${escHtml(e.tone)}</td>
      </tr>`).join('')
    sections.push(`
      <h2 class="section-title">Communication Log (${comms.length} entries — ${hostileCount} hostile, ${concerningCount} concerning)</h2>
      <table>
        <thead><tr><th style="width:12%">Date</th><th>Summary</th><th style="width:12%">Tone</th></tr></thead>
        <tbody>${commRows}</tbody>
      </table>
      ${comms.length > 20 ? '<p style="font-size:8pt;color:#64748b;margin-top:4px">Showing 20 most recent. Full log available in separate export.</p>' : ''}`)
  }

  // ── Event Log Summary ──
  if (events?.length > 0) {
    const violations = events.filter(e => e.type === 'violation').length
    const missed = events.filter(e => e.type === 'missed').length
    const recentEvents = [...events].sort((a, b) => b.date?.localeCompare(a.date)).slice(0, 20)
    const eventRows = recentEvents.map((e, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${escHtml(e.date)}</td>
        <td>${escHtml(e.type)}</td>
        <td>${escHtml(e.description?.slice(0, 80))}${e.description?.length > 80 ? '…' : ''}</td>
        <td style="text-align:center">${e.evidence ? '✅' : '—'}</td>
      </tr>`).join('')
    sections.push(`
      <h2 class="section-title">Custody Events (${events.length} entries — ${violations} violations, ${missed} missed visits)</h2>
      <table>
        <thead><tr><th style="width:12%">Date</th><th style="width:16%">Type</th><th>Description</th><th style="width:8%">Evidence</th></tr></thead>
        <tbody>${eventRows}</tbody>
      </table>
      ${events.length > 20 ? '<p style="font-size:8pt;color:#64748b;margin-top:4px">Showing 20 most recent. Full log available in separate export.</p>' : ''}`)
  }

  // ── Upcoming Calendar Events ──
  if (calendarEvents?.length > 0) {
    const todayStr = new Date().toISOString().split('T')[0]
    const upcoming = calendarEvents.filter(e => e.start_date >= todayStr).sort((a, b) => a.start_date.localeCompare(b.start_date)).slice(0, 15)
    if (upcoming.length > 0) {
      const calRows = upcoming.map((e, i) => `
        <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
          <td>${escHtml(e.start_date)}${e.start_time ? ' ' + escHtml(e.start_time) : ''}</td>
          <td>${escHtml(e.type)}</td>
          <td>${escHtml(e.title)}</td>
          <td>${escHtml(e.location || '—')}</td>
        </tr>`).join('')
      sections.push(`
        <h2 class="section-title">Upcoming Calendar Events (${upcoming.length})</h2>
        <table>
          <thead><tr><th style="width:16%">Date/Time</th><th style="width:18%">Type</th><th>Title</th><th style="width:20%">Location</th></tr></thead>
          <tbody>${calRows}</tbody>
        </table>`)
    }
  }

  // ── Expense Summary ──
  if (expenses?.length > 0) {
    function fmtMoney(n) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
    }
    const totals = expenses.reduce((acc, e) => {
      acc.total += e.total_amount || 0
      const isMyExpense = e.submitted_by === currentUserId
      if (isMyExpense) acc.theyOwe += e.total_amount * (100 - e.payer_pct) / 100
      else             acc.youOwe  += e.total_amount * e.payer_pct / 100
      return acc
    }, { total: 0, theyOwe: 0, youOwe: 0 })
    const recent = [...expenses].sort((a, b) => (b.purchase_date || '').localeCompare(a.purchase_date || '')).slice(0, 15)
    const expRows = recent.map((e, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${escHtml(e.purchase_date || '—')}</td>
        <td>${escHtml(e.title)}</td>
        <td style="text-align:right">${fmtMoney(e.total_amount)}</td>
        <td>${escHtml(e.status)}</td>
      </tr>`).join('')
    sections.push(`
      <h2 class="section-title">Expense Summary (${expenses.length} expenses — Total: ${fmtMoney(totals.total)})</h2>
      <table>
        <thead><tr><th style="width:12%">Date</th><th>Expense</th><th style="width:14%;text-align:right">Amount</th><th style="width:12%">Status</th></tr></thead>
        <tbody>${expRows}</tbody>
      </table>
      ${expenses.length > 15 ? '<p style="font-size:8pt;color:#64748b;margin-top:4px">Showing 15 most recent. Full expense log available in separate export.</p>' : ''}`)
  }

  if (sections.length === 0) {
    sections.push('<p style="color:#94a3b8;text-align:center;padding:30px">No data to include in report. Start logging communications, events, and expenses to build your case file.</p>')
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Comprehensive Court Report — GoodGuardian</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #1e293b; background: #fff; }
    .header { border-bottom: 3px solid ${BRAND}; padding-bottom: 10px; margin-bottom: 16px; }
    .header .logo { font-size: 9pt; font-weight: bold; color: ${BRAND}; letter-spacing: 1px; text-transform: uppercase; }
    .header h1 { font-size: 17pt; color: ${BRAND}; margin: 4px 0 2px; }
    .header .sub { font-size: 8.5pt; color: #64748b; }
    .section-title { font-size: 12pt; font-weight: bold; color: ${BRAND}; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px; margin: 22px 0 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 8px; }
    th { background: ${BRAND}; color: #fff; font-weight: bold; text-align: left; padding: 5px 8px; }
    td { padding: 4px 8px; vertical-align: top; border-bottom: 1px solid #e2e8f0; line-height: 1.4; }
    tr.even td { background: #f8fafc; }
    small { font-size: 7.5pt; color: #94a3b8; }
    .disclaimer { margin-top: 22px; padding: 10px 12px; border: 1px solid #fbbf24; background: #fffbeb; border-radius: 5px; font-size: 8pt; color: #78350f; line-height: 1.5; }
    .footer { margin-top: 14px; font-size: 7.5pt; color: #94a3b8; text-align: center; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { margin: 0.75in; }
      .no-print { display: none; }
      .section-title { page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="background:#1F497D;color:#fff;padding:10px 16px;font-size:10pt;display:flex;justify-content:space-between;align-items:center">
    <span>🛡️ <strong>GoodGuardian</strong> — Comprehensive Court Report</span>
    <button onclick="window.print()" style="background:#fff;color:#1F497D;border:none;padding:6px 16px;border-radius:20px;font-weight:bold;cursor:pointer;font-size:10pt">
      🖨️ Save as PDF / Print
    </button>
  </div>
  <div style="padding: 20px 24px">
    <div class="header">
      <div class="logo">🛡️ GoodGuardian</div>
      <h1>Comprehensive Court Report</h1>
      <div class="sub">Prepared by: ${escHtml(userName || 'GoodGuardian User')}  |  Jurisdiction: ${escHtml(state || 'Not specified')}  |  Generated: ${printDate}${escHtml(rangeLabel)}</div>
    </div>
    ${sections.join('\n')}
    <div class="disclaimer">
      <strong>⚠️ Legal Disclaimer:</strong> This comprehensive report was generated by GoodGuardian (good-guardian.net). It compiles the account holder's contemporaneous records including communications, custody events, calendar entries, and expenses. All entries are timestamped at the time of creation. This document has not been reviewed by a licensed attorney. Share with your attorney before submitting to any court.
    </div>
    <div class="footer">Generated by GoodGuardian · good-guardian.net · AI-powered family law guidance · Not legal advice</div>
  </div>
</body>
</html>`

  openPrintWindow(html)
}

// ── Medical Records export ────────────────────────────────────────────────────
export function printMedicalRecords({ child, records }) {
  const printDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const TYPE_LABELS = {
    visit: '🏥 Doctor Visit', emergency: '🚨 ER / Emergency', dental: '🦷 Dental',
    vision: '👁️ Vision', therapy: '🧠 Therapy', vaccination: '💉 Vaccination',
    specialist: '🔬 Specialist', medication: '💊 Medication Change',
    denied: '🚫 Access Denied', other: '📋 Other',
  }

  const stats = {
    total:    records.length,
    er:       records.filter(r => r.record_type === 'emergency').length,
    denied:   records.filter(r => r.record_type === 'denied').length,
    thisYear: records.filter(r => r.date?.startsWith(new Date().getFullYear().toString())).length,
  }

  const rows = records.map((r, i) => {
    const isDenied = r.record_type === 'denied'
    return `
    <tr class="${i % 2 === 0 ? 'even' : 'odd'}${isDenied ? ' denied-row' : ''}">
      <td>${escHtml(r.date || '—')}</td>
      <td>${escHtml(TYPE_LABELS[r.record_type] || r.record_type)}${isDenied ? ' <strong style="color:#b91c1c">⚖️</strong>' : ''}</td>
      <td>${escHtml(r.provider_name || '—')}${r.provider_phone ? '<br><small>' + escHtml(r.provider_phone) + '</small>' : ''}</td>
      <td>${escHtml(r.diagnosis || '—')}</td>
      <td>${escHtml(r.treatment || '—')}</td>
      <td>${escHtml(r.notes || '—')}</td>
    </tr>`
  }).join('')

  // Child health summary block
  const summaryItems = [
    child.blood_type         && `<strong>Blood Type:</strong> ${escHtml(child.blood_type)}`,
    child.allergies          && `<strong>Allergies:</strong> ${escHtml(child.allergies)}`,
    child.medications        && `<strong>Medications:</strong> ${escHtml(child.medications)}`,
    child.pediatrician_name  && `<strong>Pediatrician:</strong> ${escHtml(child.pediatrician_name)}${child.pediatrician_phone ? ' · ' + escHtml(child.pediatrician_phone) : ''}`,
    child.insurance_provider && `<strong>Insurance:</strong> ${escHtml(child.insurance_provider)}${child.insurance_id ? ' · ID: ' + escHtml(child.insurance_id) : ''}`,
  ].filter(Boolean)

  const summaryHtml = summaryItems.length > 0 ? `
    <div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:9pt;line-height:1.8">
      <strong style="display:block;margin-bottom:4px;color:#1F497D">📋 ${escHtml(child.name)}'s Health Summary</strong>
      ${summaryItems.join('<br>')}
    </div>` : ''

  const deniedSection = stats.denied > 0 ? `
    <div style="background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:9pt;color:#991b1b">
      <strong>⚖️ ${stats.denied} Denied Access ${stats.denied === 1 ? 'Incident' : 'Incidents'} on Record</strong><br>
      These records document instances where the account holder was denied access to medical information or decision-making. They are flagged as court-relevant evidence of unilateral medical decision-making by the other parent.
    </div>` : ''

  const html = buildPrintPage({
    title:    `Medical Records — ${child.name}`,
    subtitle: `Prepared for: ${escHtml(child.name)}  |  Generated: ${printDate}  |  Records logged by account holder`,
    statsHtml: `
      <div class="stats">
        <div class="stat"><span class="num">${stats.total}</span>Total Records</div>
        <div class="stat warn"><span class="num">${stats.er}</span>ER / Emergency</div>
        <div class="stat warn"><span class="num">${stats.denied}</span>Access Denied</div>
        <div class="stat"><span class="num">${stats.thisYear}</span>This Year</div>
      </div>
      ${summaryHtml}
      ${deniedSection}`,
    tableHtml: records.length === 0 ? '<p style="color:#94a3b8;text-align:center;padding:20px">No records to display.</p>' : `
      <style>.denied-row td { background: #fef2f2 !important; }</style>
      <table>
        <thead>
          <tr>
            <th style="width:10%">Date</th>
            <th style="width:16%">Type</th>
            <th style="width:18%">Provider</th>
            <th style="width:22%">Diagnosis / Detail</th>
            <th style="width:18%">Treatment</th>
            <th style="width:16%">Notes</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`,
    disclaimer: `This medical record log was generated by GoodGuardian (good-guardian.net). Records reflect the account holder's contemporaneous documentation of their child's medical history, appointments, and any incidents of denied access. Every record access has been logged per HIPAA-awareness practices. This document has not been reviewed by a licensed attorney or healthcare provider. Share with your attorney and your child's healthcare providers as appropriate.`,
  })

  openPrintWindow(html)
}

// ── Case Timeline export ──────────────────────────────────────────────────────
export function printCaseTimeline({ userName, state, items, rangeLabel }) {
  const printDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const SOURCE_LABEL = {
    event: 'Custody Event', comm: 'Communication', violation: 'Order Violation',
    wellbeing: 'Wellbeing', calendar: 'Calendar', order: 'Court Order',
  }
  const SOURCE_COLOR = {
    event: '#3b82f6', comm: '#64748b', violation: '#f97316',
    wellbeing: '#8b5cf6', calendar: '#2E75B6', order: '#10b981',
  }

  // Group items by date (they arrive pre-sorted newest-first; reverse for print)
  const byDate = new Map()
  for (const item of [...items].sort((a, b) => a.sortKey.localeCompare(b.sortKey))) {
    if (!byDate.has(item.date)) byDate.set(item.date, [])
    byDate.get(item.date).push(item)
  }

  const dateGroups = [...byDate.entries()].sort((a, b) => a[0].localeCompare(b[0]))

  const sections = dateGroups.map(([date, dayItems]) => {
    const [y, m, d] = date.split('-').map(Number)
    const label = new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    })
    const rows = dayItems.map((item, i) => {
      const badges = item.extraBadges?.map(b => escHtml(b.text)).join(' &nbsp;·&nbsp; ') || ''
      const color  = SOURCE_COLOR[item.source] || '#64748b'
      return `
        <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
          <td style="border-left: 4px solid ${color}; padding-left: 8px; white-space:nowrap">
            ${escHtml(item.icon)} ${escHtml(SOURCE_LABEL[item.source] || item.source)}
            ${item.time ? '<br><small>' + escHtml(item.time) + '</small>' : ''}
          </td>
          <td><strong>${escHtml(item.title)}</strong>${item.summary ? '<br><small>' + escHtml(item.summary.slice(0, 120)) + (item.summary.length > 120 ? '…' : '') + '</small>' : ''}</td>
          <td>${escHtml(item.badgeText)}</td>
          <td style="font-size:8pt;color:#475569">${badges}</td>
        </tr>`
    }).join('')
    return `
      <h2 class="section-title">${escHtml(label)} <span style="font-weight:normal;font-size:10pt">(${dayItems.length} ${dayItems.length === 1 ? 'entry' : 'entries'})</span></h2>
      <table>
        <thead>
          <tr>
            <th style="width:14%">Type</th>
            <th style="width:40%">Details</th>
            <th style="width:14%">Status / Severity</th>
            <th style="width:32%">Context</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`
  }).join('')

  const stats = {
    total:      items.length,
    violations: items.filter(i => i.source === 'violation').length,
    hostile:    items.filter(i => i.source === 'comm' && i.flag).length,
    flagged:    items.filter(i => i.flag).length,
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Case Timeline — GoodGuardian</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #1e293b; background: #fff; }
    .header { border-bottom: 3px solid #1F497D; padding-bottom: 10px; margin-bottom: 16px; }
    .header .logo { font-size: 9pt; font-weight: bold; color: #1F497D; letter-spacing: 1px; text-transform: uppercase; }
    .header h1 { font-size: 17pt; color: #1F497D; margin: 4px 0 2px; }
    .header .sub { font-size: 8.5pt; color: #64748b; }
    .stats { display: flex; gap: 16px; margin-bottom: 14px; }
    .stat { background: #f1f5f9; border-radius: 6px; padding: 8px 14px; text-align: center; min-width: 90px; }
    .stat .num { display: block; font-size: 20pt; font-weight: bold; color: #1F497D; line-height: 1; }
    .stat { font-size: 8pt; color: #475569; }
    .stat.warn .num { color: #b91c1c; }
    .section-title { font-size: 11pt; font-weight: bold; color: #1F497D; border-bottom: 1px solid #e2e8f0; padding: 6px 0 3px; margin: 18px 0 8px; }
    table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 4px; }
    th { background: #1F497D; color: #fff; font-weight: bold; text-align: left; padding: 5px 8px; }
    td { padding: 4px 8px; vertical-align: top; border-bottom: 1px solid #e2e8f0; line-height: 1.4; }
    tr.even td { background: #f8fafc; }
    small { font-size: 7.5pt; color: #94a3b8; }
    .disclaimer { margin-top: 22px; padding: 10px 12px; border: 1px solid #fbbf24; background: #fffbeb; border-radius: 5px; font-size: 8pt; color: #78350f; line-height: 1.5; }
    .footer { margin-top: 14px; font-size: 7.5pt; color: #94a3b8; text-align: center; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { margin: 0.75in; }
      .no-print { display: none; }
      .section-title { page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="background:#1F497D;color:#fff;padding:10px 16px;font-size:10pt;display:flex;justify-content:space-between;align-items:center">
    <span>🛡️ <strong>GoodGuardian</strong> — Case Timeline</span>
    <button onclick="window.print()" style="background:#fff;color:#1F497D;border:none;padding:6px 16px;border-radius:20px;font-weight:bold;cursor:pointer;font-size:10pt">
      🖨️ Save as PDF / Print
    </button>
  </div>
  <div style="padding: 20px 24px">
    <div class="header">
      <div class="logo">🛡️ GoodGuardian</div>
      <h1>Case Timeline</h1>
      <div class="sub">Prepared by: ${escHtml(userName || 'GoodGuardian User')}  |  Jurisdiction: ${escHtml(state || 'Not specified')}  |  Period: ${escHtml(rangeLabel)}  |  Generated: ${printDate}</div>
    </div>
    <div class="stats">
      <div class="stat"><span class="num">${stats.total}</span>Total Entries</div>
      <div class="stat warn"><span class="num">${stats.violations}</span>Violations</div>
      <div class="stat warn"><span class="num">${stats.hostile}</span>Hostile Comms</div>
      <div class="stat warn"><span class="num">${stats.flagged}</span>Flagged</div>
    </div>
    ${sections.length > 0 ? sections : '<p style="color:#94a3b8;text-align:center;padding:30px">No entries in the selected date range.</p>'}
    <div class="disclaimer">
      <strong>⚠️ Legal Disclaimer:</strong> This case timeline was generated by GoodGuardian (good-guardian.net). It compiles the account holder's contemporaneous records including custody events, communications, order violations, wellbeing check-ins, and calendar events. All entries are timestamped at the time of creation. This document has not been reviewed by a licensed attorney. Share with your attorney before submitting to any court.
    </div>
    <div class="footer">Generated by GoodGuardian · good-guardian.net · AI-powered family law guidance · Not legal advice</div>
  </div>
</body>
</html>`

  openPrintWindow(html)
}

// ── Shared helpers ─────────────────────────────────────────────────────────────
function buildPrintPage({ title, subtitle, statsHtml, tableHtml, disclaimer }) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${escHtml(title)} — GoodGuardian</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #1e293b; background: #fff; }
    .header { border-bottom: 3px solid ${BRAND}; padding-bottom: 10px; margin-bottom: 16px; }
    .header .logo { font-size: 9pt; font-weight: bold; color: ${BRAND}; letter-spacing: 1px; text-transform: uppercase; }
    .header h1 { font-size: 17pt; color: ${BRAND}; margin: 4px 0 2px; }
    .header .sub { font-size: 8.5pt; color: #64748b; }
    .stats { display: flex; gap: 16px; margin-bottom: 14px; }
    .stat { background: #f1f5f9; border-radius: 6px; padding: 8px 14px; text-align: center; min-width: 90px; }
    .stat .num { display: block; font-size: 20pt; font-weight: bold; color: ${BRAND}; line-height: 1; }
    .stat { font-size: 8pt; color: #475569; }
    .stat.warn .num { color: #b91c1c; }
    table { width: 100%; border-collapse: collapse; font-size: 9pt; }
    th { background: ${BRAND}; color: #fff; font-weight: bold; text-align: left; padding: 6px 8px; }
    td { padding: 5px 8px; vertical-align: top; border-bottom: 1px solid #e2e8f0; line-height: 1.4; }
    tr.even td { background: #f8fafc; }
    small { font-size: 7.5pt; color: #94a3b8; }
    .disclaimer { margin-top: 18px; padding: 10px 12px; border: 1px solid #fbbf24; background: #fffbeb; border-radius: 5px; font-size: 8pt; color: #78350f; line-height: 1.5; }
    .disclaimer strong { font-weight: bold; }
    .footer { margin-top: 14px; font-size: 7.5pt; color: #94a3b8; text-align: center; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { margin: 0.75in; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="background:#1F497D;color:#fff;padding:10px 16px;font-size:10pt;display:flex;justify-content:space-between;align-items:center">
    <span>🛡️ <strong>GoodGuardian</strong> — Court-Ready Export</span>
    <button onclick="window.print()" style="background:#fff;color:#1F497D;border:none;padding:6px 16px;border-radius:20px;font-weight:bold;cursor:pointer;font-size:10pt">
      🖨️ Save as PDF / Print
    </button>
  </div>
  <div style="padding: 20px 24px">
    <div class="header">
      <div class="logo">🛡️ GoodGuardian</div>
      <h1>${escHtml(title)}</h1>
      <div class="sub">${escHtml(subtitle)}</div>
    </div>
    ${statsHtml}
    ${tableHtml}
    <div class="disclaimer">
      <strong>⚠️ Legal Disclaimer:</strong> ${escHtml(disclaimer)}
    </div>
    <div class="footer">Generated by GoodGuardian · good-guardian.net · AI-powered family law guidance · Not legal advice</div>
  </div>
</body>
</html>`
}

function openPrintWindow(html) {
  const win = window.open('', '_blank', 'width=900,height=700')
  if (!win) {
    alert('Pop-up blocked. Please allow pop-ups for this site, then try again.')
    return
  }
  win.document.write(html)
  win.document.close()
  win.focus()
}
